/**
 * ISO 3166-1 alpha-2 country-code normalisation.
 *
 * Copy of `libs/utils/src/lib/country-codes.ts` — the frontend build
 * (react-scripts) cannot import outside `src/`, and restricted-country checks
 * must resolve countries exactly like the backend does so the UI blocks the
 * same trades the API rejects. Keep the two files in sync.
 *
 * User addresses and Greenwich restricted-country entries hold either a
 * two-letter code ("GB") or a free-text label ("United Kingdom", "UK").
 * `toIso3166Alpha2` accepts a label, an alias, or an already-valid code and
 * returns the canonical alpha-2, or `undefined` when it can't confidently
 * resolve one — callers should treat that as a missing country rather than
 * guessing.
 */

/**
 * Canonical country-name → ISO 3166-1 alpha-2. Keys are normalised (see
 * `normalize`) so callers don't have to worry about case/punctuation. Common
 * aliases ("USA", "UK", "UAE", "Holland", …) are folded in alongside the
 * official short names.
 */
const COUNTRY_NAME_TO_ALPHA2: Readonly<Record<string, string>> = {
  AFGHANISTAN: "AF",
  "ALAND ISLANDS": "AX",
  ALBANIA: "AL",
  ALGERIA: "DZ",
  "AMERICAN SAMOA": "AS",
  ANDORRA: "AD",
  ANGOLA: "AO",
  ANGUILLA: "AI",
  ANTARCTICA: "AQ",
  "ANTIGUA AND BARBUDA": "AG",
  ARGENTINA: "AR",
  ARMENIA: "AM",
  ARUBA: "AW",
  AUSTRALIA: "AU",
  AUS: "AU",
  AUSTRIA: "AT",
  AZERBAIJAN: "AZ",
  BAHAMAS: "BS",
  "THE BAHAMAS": "BS",
  BAHRAIN: "BH",
  BANGLADESH: "BD",
  BARBADOS: "BB",
  BELARUS: "BY",
  BELGIUM: "BE",
  BELIZE: "BZ",
  BENIN: "BJ",
  BERMUDA: "BM",
  BHUTAN: "BT",
  BOLIVIA: "BO",
  "BONAIRE SINT EUSTATIUS AND SABA": "BQ",
  "BOSNIA AND HERZEGOVINA": "BA",
  BOTSWANA: "BW",
  "BOUVET ISLAND": "BV",
  BRAZIL: "BR",
  "BRITISH INDIAN OCEAN TERRITORY": "IO",
  "BRUNEI DARUSSALAM": "BN",
  BRUNEI: "BN",
  BULGARIA: "BG",
  "BURKINA FASO": "BF",
  BURUNDI: "BI",
  "CABO VERDE": "CV",
  "CAPE VERDE": "CV",
  CAMBODIA: "KH",
  CAMEROON: "CM",
  CANADA: "CA",
  "CAYMAN ISLANDS": "KY",
  "CENTRAL AFRICAN REPUBLIC": "CF",
  CHAD: "TD",
  CHILE: "CL",
  CHINA: "CN",
  "CHRISTMAS ISLAND": "CX",
  "COCOS ISLANDS": "CC",
  "COCOS KEELING ISLANDS": "CC",
  COLOMBIA: "CO",
  COMOROS: "KM",
  CONGO: "CG",
  "CONGO REPUBLIC OF THE": "CG",
  "CONGO DEMOCRATIC REPUBLIC OF THE": "CD",
  "DEMOCRATIC REPUBLIC OF THE CONGO": "CD",
  "COOK ISLANDS": "CK",
  "COSTA RICA": "CR",
  "COTE DIVOIRE": "CI",
  "IVORY COAST": "CI",
  CROATIA: "HR",
  CUBA: "CU",
  CURACAO: "CW",
  CYPRUS: "CY",
  CZECHIA: "CZ",
  "CZECH REPUBLIC": "CZ",
  DENMARK: "DK",
  DJIBOUTI: "DJ",
  DOMINICA: "DM",
  "DOMINICAN REPUBLIC": "DO",
  ECUADOR: "EC",
  EGYPT: "EG",
  "EL SALVADOR": "SV",
  "EQUATORIAL GUINEA": "GQ",
  ERITREA: "ER",
  ESTONIA: "EE",
  ESWATINI: "SZ",
  SWAZILAND: "SZ",
  ETHIOPIA: "ET",
  "FALKLAND ISLANDS": "FK",
  "FAROE ISLANDS": "FO",
  FIJI: "FJ",
  FINLAND: "FI",
  FRANCE: "FR",
  "FRENCH GUIANA": "GF",
  "FRENCH POLYNESIA": "PF",
  "FRENCH SOUTHERN TERRITORIES": "TF",
  GABON: "GA",
  GAMBIA: "GM",
  "THE GAMBIA": "GM",
  GEORGIA: "GE",
  GERMANY: "DE",
  GHANA: "GH",
  GIBRALTAR: "GI",
  GREECE: "GR",
  GREENLAND: "GL",
  GRENADA: "GD",
  GUADELOUPE: "GP",
  GUAM: "GU",
  GUATEMALA: "GT",
  GUERNSEY: "GG",
  GUINEA: "GN",
  "GUINEA-BISSAU": "GW",
  "GUINEA BISSAU": "GW",
  GUYANA: "GY",
  HAITI: "HT",
  "HEARD ISLAND AND MCDONALD ISLANDS": "HM",
  "HOLY SEE": "VA",
  "VATICAN CITY": "VA",
  HONDURAS: "HN",
  "HONG KONG": "HK",
  HUNGARY: "HU",
  ICELAND: "IS",
  INDIA: "IN",
  INDONESIA: "ID",
  IRAN: "IR",
  IRAQ: "IQ",
  IRELAND: "IE",
  "ISLE OF MAN": "IM",
  ISRAEL: "IL",
  ITALY: "IT",
  JAMAICA: "JM",
  JAPAN: "JP",
  JERSEY: "JE",
  JORDAN: "JO",
  KAZAKHSTAN: "KZ",
  KENYA: "KE",
  KIRIBATI: "KI",
  "NORTH KOREA": "KP",
  "KOREA DEMOCRATIC PEOPLES REPUBLIC OF": "KP",
  "SOUTH KOREA": "KR",
  "KOREA REPUBLIC OF": "KR",
  KUWAIT: "KW",
  KYRGYZSTAN: "KG",
  LAOS: "LA",
  "LAO PEOPLES DEMOCRATIC REPUBLIC": "LA",
  LATVIA: "LV",
  LEBANON: "LB",
  LESOTHO: "LS",
  LIBERIA: "LR",
  LIBYA: "LY",
  LIECHTENSTEIN: "LI",
  LITHUANIA: "LT",
  LUXEMBOURG: "LU",
  MACAO: "MO",
  MACAU: "MO",
  MADAGASCAR: "MG",
  MALAWI: "MW",
  MALAYSIA: "MY",
  MALDIVES: "MV",
  MALI: "ML",
  MALTA: "MT",
  "MARSHALL ISLANDS": "MH",
  MARTINIQUE: "MQ",
  MAURITANIA: "MR",
  MAURITIUS: "MU",
  MAYOTTE: "YT",
  MEXICO: "MX",
  MICRONESIA: "FM",
  MOLDOVA: "MD",
  MONACO: "MC",
  MONGOLIA: "MN",
  MONTENEGRO: "ME",
  MONTSERRAT: "MS",
  MOROCCO: "MA",
  MOZAMBIQUE: "MZ",
  MYANMAR: "MM",
  BURMA: "MM",
  NAMIBIA: "NA",
  NAURU: "NR",
  NEPAL: "NP",
  NETHERLANDS: "NL",
  "THE NETHERLANDS": "NL",
  HOLLAND: "NL",
  "NEW CALEDONIA": "NC",
  "NEW ZEALAND": "NZ",
  NICARAGUA: "NI",
  NIGER: "NE",
  NIGERIA: "NG",
  NIUE: "NU",
  "NORFOLK ISLAND": "NF",
  "NORTH MACEDONIA": "MK",
  MACEDONIA: "MK",
  "NORTHERN MARIANA ISLANDS": "MP",
  NORWAY: "NO",
  OMAN: "OM",
  PAKISTAN: "PK",
  PALAU: "PW",
  PALESTINE: "PS",
  "PALESTINE STATE OF": "PS",
  PANAMA: "PA",
  "PAPUA NEW GUINEA": "PG",
  PARAGUAY: "PY",
  PERU: "PE",
  PHILIPPINES: "PH",
  "THE PHILIPPINES": "PH",
  PITCAIRN: "PN",
  POLAND: "PL",
  PORTUGAL: "PT",
  "PUERTO RICO": "PR",
  QATAR: "QA",
  REUNION: "RE",
  ROMANIA: "RO",
  "RUSSIAN FEDERATION": "RU",
  RUSSIA: "RU",
  RWANDA: "RW",
  "SAINT BARTHELEMY": "BL",
  "SAINT HELENA": "SH",
  "SAINT KITTS AND NEVIS": "KN",
  "SAINT LUCIA": "LC",
  "SAINT MARTIN": "MF",
  "SAINT PIERRE AND MIQUELON": "PM",
  "SAINT VINCENT AND THE GRENADINES": "VC",
  SAMOA: "WS",
  "SAN MARINO": "SM",
  "SAO TOME AND PRINCIPE": "ST",
  "SAUDI ARABIA": "SA",
  SENEGAL: "SN",
  SERBIA: "RS",
  SEYCHELLES: "SC",
  "SIERRA LEONE": "SL",
  SINGAPORE: "SG",
  "SINT MAARTEN": "SX",
  SLOVAKIA: "SK",
  SLOVENIA: "SI",
  "SOLOMON ISLANDS": "SB",
  SOMALIA: "SO",
  "SOUTH AFRICA": "ZA",
  "SOUTH GEORGIA AND THE SOUTH SANDWICH ISLANDS": "GS",
  "SOUTH SUDAN": "SS",
  SPAIN: "ES",
  "SRI LANKA": "LK",
  SUDAN: "SD",
  SURINAME: "SR",
  "SVALBARD AND JAN MAYEN": "SJ",
  SWEDEN: "SE",
  SWITZERLAND: "CH",
  SYRIA: "SY",
  "SYRIAN ARAB REPUBLIC": "SY",
  TAIWAN: "TW",
  TAJIKISTAN: "TJ",
  TANZANIA: "TZ",
  THAILAND: "TH",
  "TIMOR-LESTE": "TL",
  "TIMOR LESTE": "TL",
  "EAST TIMOR": "TL",
  TOGO: "TG",
  TOKELAU: "TK",
  TONGA: "TO",
  "TRINIDAD AND TOBAGO": "TT",
  TUNISIA: "TN",
  TURKEY: "TR",
  TURKIYE: "TR",
  TURKMENISTAN: "TM",
  "TURKS AND CAICOS ISLANDS": "TC",
  TUVALU: "TV",
  UGANDA: "UG",
  UKRAINE: "UA",
  "UNITED ARAB EMIRATES": "AE",
  UAE: "AE",
  "UNITED KINGDOM": "GB",
  "UNITED KINGDOM OF GREAT BRITAIN AND NORTHERN IRELAND": "GB",
  UK: "GB",
  "GREAT BRITAIN": "GB",
  "UNITED STATES": "US",
  "UNITED STATES OF AMERICA": "US",
  USA: "US",
  "U S A": "US",
  AMERICA: "US",
  "UNITED STATES MINOR OUTLYING ISLANDS": "UM",
  URUGUAY: "UY",
  UZBEKISTAN: "UZ",
  VANUATU: "VU",
  VENEZUELA: "VE",
  VIETNAM: "VN",
  "VIET NAM": "VN",
  "VIRGIN ISLANDS BRITISH": "VG",
  "BRITISH VIRGIN ISLANDS": "VG",
  "VIRGIN ISLANDS US": "VI",
  "US VIRGIN ISLANDS": "VI",
  "WALLIS AND FUTUNA": "WF",
  "WESTERN SAHARA": "EH",
  YEMEN: "YE",
  ZAMBIA: "ZM",
  ZIMBABWE: "ZW",
};

/** Set of every valid alpha-2 code we know about, derived from the map values. */
const VALID_ALPHA2 = new Set(Object.values(COUNTRY_NAME_TO_ALPHA2));

/**
 * Normalise a free-text country label for lookup: uppercase, drop any
 * parenthetical suffix (e.g. "United States of America (USA)" → "UNITED STATES
 * OF AMERICA"), strip punctuation, and collapse whitespace.
 */
function normalize(input: string): string {
  return input
    .toUpperCase()
    .replace(/\([^)]*\)/g, " ")
    .replace(/[.,'’`]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Resolve a country label, alias, or already-valid code to its ISO 3166-1
 * alpha-2 code. Returns `undefined` when the input is empty or can't be
 * confidently mapped — callers should treat that as a missing country.
 */
export function toIso3166Alpha2(input: unknown): string | undefined {
  if (typeof input !== "string") return undefined;
  const trimmed = input.trim();
  if (!trimmed) return undefined;

  // Already a real two-letter ISO code — pass through (uppercased).
  if (/^[A-Za-z]{2}$/.test(trimmed) && VALID_ALPHA2.has(trimmed.toUpperCase())) {
    return trimmed.toUpperCase();
  }

  // Otherwise resolve by name/alias ("UK" → GB, "USA" → US, "Australia" → AU).
  return COUNTRY_NAME_TO_ALPHA2[normalize(trimmed)];
}
